import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';

import { AdminUserRoleEnum, SalesRepUserRoleEnum, Agency } from '__generated-api__';
import api from 'api';
import { useAbility, useAuthenticatedUser } from 'auth';
import { useQuery } from 'hooks/query';
import Icon from 'components/icon';
import { MainHero } from 'my-account/components/MainHero';
import { EditAgencyForm } from 'my-account/forms/Agency';
import { Block403Error } from 'my-account/pages/403page';
import { ErrorMessagesContent } from 'my-account/utils/error-handler';
import useDocumentTitle from 'hooks/useDocumentTitle';

export const AgencyTabsNav: React.VFC<{ agency: Agency; activeTab: 'edit' | 'sales-goal-report' }> = ({
  agency,
  activeTab,
}) => {
  const { goals } = agency;

  if (!goals || !goals.length) {
    return null;
  }

  const detailsUrl = `/agencies/${agency.id}`;
  const salesGoalReportUrl = `/agencies/${agency.id}/sales-goal-report`;
  const hasSalesGoalReport = !!goals.length;

  if (!hasSalesGoalReport) {
    return null;
  }

  return (
    <nav className="c-tabs-nav c-tabs-nav--underline u-mb-0 u-mt-spacer-base u-mt-0@md u-justify-end@md">
      <ul className="c-tabs-nav-menu">
        <li className={classNames({ 'is-selected': activeTab === 'edit' })}>
          <Link to={detailsUrl}>
            <span>General</span>
          </Link>
        </li>
        <li className={classNames({ 'is-selected': activeTab === 'sales-goal-report' })}>
          <Link to={salesGoalReportUrl}>
            <span>Agency Report</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default function AgencyEditPage() {
  const user = useAuthenticatedUser();
  const { id } = useParams<{ id?: string }>();
  const ability = useAbility();
  const [agencyRes, getAgencyState] = useQuery(api.agency.getAgency, { id: Number(id) });
  useDocumentTitle(agencyRes?.data.name ? `Edit ${agencyRes?.data.name} Agency` : 'Edit Agency');

  const canViewAgenciesList = ability.can('list', 'Agency');
  const canUpdate = ability.can('update', 'Agency');

  const showTabs =
    (user.role === AdminUserRoleEnum.Admin || user.role === SalesRepUserRoleEnum.SalesRep) &&
    typeof agencyRes !== 'undefined' &&
    getAgencyState.isSuccess;

  return (
    <>
      <MainHero />

      <section className="c-block c-block--hero">
        <div className="o-container-fluid">
          <div className="o-row u-flex-grow u-items-end">
            <div className="o-col-12">
              {canViewAgenciesList && (
                <Link
                  to="/agencies"
                  className="c-link-cta-basic c-link-cta--small c-block__get-back u-mt-spacer-base-large u-mb-spacer-base-small"
                >
                  <Icon name="arrow" className="o-svg-icon o-svg-left" />
                  <span>Back to agencies</span>
                </Link>
              )}
              <h1 className="c-title--medium u-mb-0">{canUpdate ? 'Edit Agency' : 'Agency'}</h1>
            </div>
          </div>
          {showTabs && <AgencyTabsNav agency={agencyRes.data} activeTab="edit" />}
        </div>
        <div className="c-block__pattern c-block__pattern--light3 c-block__pattern--top3 c-block__overlay--opacity-20">
          <Icon name="pattern" className="o-svg-icon" />
        </div>
      </section>

      {getAgencyState.isLoading && (
        <div className="c-listing__none">
          <div className="c-listing__none-spinner"></div>
          <p className="c-listing__none-title">Loading...</p>
        </div>
      )}
      {getAgencyState.isError && (
        <div className="o-container-fluid u-pb-spacer-section">
          <div className="o-row u-flex-grow u-items-center">
            <div className="o-col-12">
              <div className="c-listing__none">
                <div className="c-listing__none-figure">
                  <Icon name="error" className="o-svg-icon" />
                </div>
                <p className="c-listing__none-title">Error</p>
                <ErrorMessagesContent error={getAgencyState.error} />
              </div>
            </div>
          </div>
        </div>
      )}
      {typeof agencyRes !== 'undefined' && getAgencyState.isSuccess && (
        <>
          {ability.can('read', subject('Agency', agencyRes.data)) ? (
            <EditAgencyForm agency={agencyRes.data} />
          ) : (
            <Block403Error />
          )}
        </>
      )}
    </>
  );
}
